import { DeleteOutlined, LinkOutlined } from "@ant-design/icons";
import { useGetBrandById } from "@entities/brand/lib/hooks/useGetBrandById";
import { nextActionToTakeEnum } from "@entities/brief/lib/constants";
import { useApproveFinanceApprovalLots } from "@entities/financeApproval/lib/hooks/useApproveFinanceApprovalLots";
import { useGetFinanceApprovalLot } from "@entities/financeApproval/lib/hooks/useGetFinanceApprovalLot";
import { usePurchaseNonProfitableLot } from "@entities/financeApproval/lib/hooks/usePurchaseNonProfitableLot";
import { useRejectFinanceApprovalLots } from "@entities/financeApproval/lib/hooks/useRejectFinanceApprovalLots";
import { ToastService } from "@shared/lib/services";
import { IDelPackage } from "@shared/lib/types";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import {
  IFinanceApprovalLotItem,
  purchaseLotStatus,
} from "@shared/lib/types/finance";
import { formatData } from "@shared/lib/utils/formatData";
import { DeliverablePillSummary } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import {
  Alert,
  Card,
  Col,
  Divider,
  Flex,
  Image,
  Input,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { generateTableData } from "./utils/generateTableData";

const { Title, Text } = Typography;

interface Props {
  lotId: string;
  onClose: () => void;
}

interface TableData {
  key: string;
  creatorName: string;
  deliverables?: IDelPackage;
  influencerCost: number;
  serviceCost: number;
  brandPrice: number;
  revenue: number;
  revenuePercentage: number;
  type: string;
  deliverableId: string;
  isBrandDeclined: boolean;
  isCreatorDeclined: boolean;
  brandDeclinedReason: string;
  creatorDeclinedReason: string;
  isCreatorRejected: boolean;
  rejectReason: string;
  isApproved: boolean;
}

export function LotView({ lotId, onClose }: Props) {
  const { lot, isFetching } = useGetFinanceApprovalLot(lotId);

  const { brand, isFetching: isFetchingBrand } = useGetBrandById(
    lot?.brandId || ""
  );

  const brandBilling = brand?.linkedBilling;

  const isBillingDifferent =
    brandBilling?.serviceChargeRate !== lot?.brandBilling?.serviceChargeRate;

  const [rejectReason, setRejectReason] = useState<string>("");
  const [bulkRejectReason, setBulkRejectReason] = useState<string>("");

  const {
    purchaseNonProfitable,
    isPurchasingNonProfitable,
    isPurchasedNonProfitable,
  } = usePurchaseNonProfitableLot(lot?.briefId || "");

  const [purchaseResult, setPurchaseResult] = useState<{
    nextActionToTake: string;
    result: {
      status: string;
      reason: string;
      briefInfluencer: IBriefInfluencerV2;
      purchaseLotId: string;
      purchaseLotItemId: string;
    }[];
  } | null>(null);
  const [showCannotPurchaseModal, setShowCannotPurchaseModal] = useState(false);
  const [showNonProfitableModal, setShowNonProfitableModal] = useState(false);

  useEffect(() => {
    if (isPurchasedNonProfitable) {
      ToastService.success("Purchased Non Profitable");
      setShowNonProfitableModal(false);
    }
  }, [isPurchasedNonProfitable]);

  const { approveLots, isApproving, variables, isApproved } =
    useApproveFinanceApprovalLots(lotId, lot?.briefId || "", (data) => {
      setPurchaseResult(data);
      if (data.nextActionToTake === nextActionToTakeEnum.CANNOT_PURCHASE) {
        setShowCannotPurchaseModal(true);
      } else if (
        data.nextActionToTake === nextActionToTakeEnum.NON_PROFITABLE
      ) {
        setShowNonProfitableModal(true);
      } else {
        ToastService.success("Purchased");
        onClose();
      }
    });

  const [deliverableModalProps, setDeliverableModalProps] = useState<{
    visible: boolean;
    deliverableId?: string;
    briefInfluencerIds: string[];
    type: "view" | "create";
  }>({
    visible: false,
    deliverableId: "",
    briefInfluencerIds: [],
    type: "create",
  });

  const {
    rejectLots,
    isRejecting,
    variables: rejectVariables,
    isRejected,
  } = useRejectFinanceApprovalLots(lotId, lot?.briefId || "");

  const initalActionableInfluencers = useMemo(() => {
    return (
      lot?.purchaseItems.filter(
        (item) =>
          item.status === purchaseLotStatus.Pending &&
          !item.briefInfluencer.isCreatorDeclined &&
          !item.briefInfluencer.isBrandDeclined
      ) || []
    );
  }, [lot?.purchaseItems]);

  const [actionableInfluencers, setActionableInfluencers] = useState<
    IFinanceApprovalLotItem[]
  >(initalActionableInfluencers);

  const isViewOnly = actionableInfluencers?.length === 0;

  const tableData: TableData[] = useMemo(() => {
    return actionableInfluencers.map((influencer) => {
      return generateTableData(
        influencer,
        lot?.brandBilling?.serviceChargeRate || 0
      );
    });
  }, [actionableInfluencers, lot?.brandBilling?.serviceChargeRate]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<TableData[]>([]);

  // Calculate totals
  const totals = tableData.reduce(
    (acc, item) => ({
      influencerCost: acc.influencerCost + item.influencerCost,
      serviceCost: acc.serviceCost + item.serviceCost,
      brandPrice: acc.brandPrice + item.brandPrice,
      revenue: acc.revenue + item.revenue,
    }),
    { influencerCost: 0, serviceCost: 0, brandPrice: 0, revenue: 0 }
  );

  const totalRevenuePercentage =
    totals.brandPrice > 0 ? (totals.revenue / totals.brandPrice) * 100 : 0;

  // Row selection configuration
  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: TableData[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: TableData) => ({
      // Exclude total row from selection
      disabled: record.key === "total",
    }),
  };

  // Calculate aggregates for selected rows (excluding total row)
  const selectedAggregates = selectedRows
    .filter((row) => row.key !== "total")
    .reduce(
      (acc, item) => ({
        influencerCost: acc.influencerCost + item.influencerCost,
        serviceCost: acc.serviceCost + item.serviceCost,
        brandPrice: acc.brandPrice + item.brandPrice,
        revenue: acc.revenue + item.revenue,
      }),
      { influencerCost: 0, serviceCost: 0, brandPrice: 0, revenue: 0 }
    );

  const selectedRevenuePercentage =
    selectedAggregates.brandPrice > 0
      ? (selectedAggregates.revenue / selectedAggregates.brandPrice) * 100
      : 0;

  const dataWithTotal = [
    ...tableData,
    {
      key: "total",
      creatorName: "Total",
      deliverables: undefined,
      influencerCost: totals.influencerCost,
      serviceCost: totals.serviceCost,
      brandPrice: totals.brandPrice,
      revenue: totals.revenue,
      revenuePercentage: totalRevenuePercentage,
      type: "",
      deliverableId: "",
      isBrandDeclined: false,
      isCreatorDeclined: false,
      brandDeclinedReason: "",
      creatorDeclinedReason: "",
      isCreatorRejected: false,
      rejectReason: "",
      isApproved: false,
    } as TableData,
  ];

  const columns: ColumnsType<TableData> = [
    {
      title: "Creator Name",
      dataIndex: "creatorName",
      key: "creatorName",
      render: (text: string, record: TableData) => (
        <Flex vertical gap={4}>
          <div>
            <TextWithTooltip text={text} limit={15} />
          </div>
          {record.type && (
            <Tag color="yellow" style={{ width: "fit-content" }}>
              {record.type}
            </Tag>
          )}
          {record.isBrandDeclined && (
            <Tooltip title={record.brandDeclinedReason}>
              <Tag color="red" style={{ width: "fit-content" }}>
                Brand Declined
              </Tag>
            </Tooltip>
          )}
          {record.isCreatorDeclined && (
            <Tooltip title={record.creatorDeclinedReason}>
              <Tag color="red" style={{ width: "fit-content" }}>
                Creator Declined
              </Tag>
            </Tooltip>
          )}
          {record.isCreatorRejected && (
            <Tooltip title={record.rejectReason}>
              <Tag color="red" style={{ width: "fit-content" }}>
                Approval Rejected
              </Tag>
            </Tooltip>
          )}
          {record.isApproved && (
            <Tooltip title={record.rejectReason}>
              <Tag color="geekblue" style={{ width: "fit-content" }}>
                Purchased
              </Tag>
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: "Deliverables",
      key: "deliverables",

      render: (_, record) => {
        if (record.deliverables) {
          return (
            <Flex align="center" gap={2}>
              <DeliverablePillSummary 
                deliverable={{
                  briefId: "",
                  purchaseItems: record?.deliverables?.purchaseItems
                }}
              />
              {/* <Flex vertical>
                <Button
                  type="link"
                  size="small"
                  className={styles.deliverableView}
                  onClick={() =>
                    setDeliverableModalProps({
                      visible: true,
                      deliverableId: record?.deliverableId || "",
                      type: "view",
                      briefInfluencerIds: [record.key],
                    })
                  }
                >
                  View
                </Button>
              </Flex> */}
            </Flex>
          );
        }
        return <div></div>;
      },
    },
    {
      title: <TextWithTooltip text="Influencer Commercial " limit={10} />,
      dataIndex: "influencerCost",
      key: "influencerCost",
      render: (value: number) => `₹ ${formatData(value, "0.00 ")}`,
    },
    {
      title: "Service Cost",
      dataIndex: "serviceCost",
      key: "serviceCost",
      render: (value: number) => `₹ ${formatData(value, "0.00 ")}`,
    },
    {
      title: "Brand Price",
      dataIndex: "brandPrice",
      key: "brandPrice",
      render: (value: number) => `₹ ${formatData(value, "0.00 ")}`,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number, record: TableData) => (
        <div>
          <div>
            ₹ {formatData(value, "0.00 ")} (
            {record.revenuePercentage.toFixed(1)}%)
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      render: (_, record) => {
        if (record.key !== "total") {
          return (
            <Popconfirm
              title="Reject Creator"
              description={
                <div>
                  <p>Are you sure you want to reject this creator?</p>
                  <Input.TextArea
                    placeholder="Enter rejection reason..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    style={{ marginTop: 8 }}
                  />
                </div>
              }
              onConfirm={() => handleReject(record.key)}
              okText="Reject"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                loading={
                  isRejecting && rejectVariables?.ids.includes(record.key)
                }
                danger
              />
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  const restInfluencers =
    lot?.purchaseItems.filter(
      (item) =>
        item.status !== purchaseLotStatus.Pending ||
        item.briefInfluencer.isCreatorDeclined ||
        item.briefInfluencer.isBrandDeclined
    ) || [];

  const restTableData: TableData[] = restInfluencers.map((influencer) => {
    const influencerCost = influencer.influencerCost?.cost || 0;
    const brandPrice = influencer.brandCost?.cost || 0;
    const serviceCost = Math.round(
      brandPrice * (lot?.brandBilling?.serviceChargeRate || 0) * 0.01
    );
    const revenue = brandPrice + serviceCost - influencerCost;
    const revenuePercentage = brandPrice > 0 ? (revenue / brandPrice) * 100 : 0;

    return {
      key: influencer._id,
      creatorName: influencer.briefInfluencer.influencerName,
      deliverables: influencer.briefInfluencer?.deliverableV2,
      influencerCost,
      serviceCost,
      brandPrice,
      revenue,
      revenuePercentage,
      type: influencer.briefInfluencer.influencer?.type || "",
      deliverableId:
        influencer.briefInfluencer?.deliverableV2?._id || "",
      isBrandDeclined: influencer.briefInfluencer.isBrandDeclined,
      isCreatorDeclined: influencer.briefInfluencer.isCreatorDeclined,
      brandDeclinedReason: influencer.briefInfluencer.brandDeclinedReason || "",
      creatorDeclinedReason:
        influencer.briefInfluencer.creatorDeclinedReason || "",
      isCreatorRejected: influencer.status === purchaseLotStatus.Rejected,
      rejectReason: influencer.rejectReason || "",
      isApproved: influencer.status === purchaseLotStatus.Approved,
    };
  });

  const restApprovedInfluencers = restTableData.filter(
    (item) => item.isApproved
  );

  const restApprovedAggregates = restApprovedInfluencers
    .filter((row) => row.key !== "total")
    .reduce(
      (acc, item) => ({
        influencerCost: acc.influencerCost + item.influencerCost,
        serviceCost: acc.serviceCost + item.serviceCost,
        brandPrice: acc.brandPrice + item.brandPrice,
        revenue: acc.revenue + item.revenue,
      }),
      { influencerCost: 0, serviceCost: 0, brandPrice: 0, revenue: 0 }
    );

  const restApprovedRevenuePercentage =
    restApprovedAggregates.brandPrice > 0
      ? (restApprovedAggregates.revenue / restApprovedAggregates.brandPrice) *
        100
      : 0;

  const handleReject = (key: string) => {
    rejectLots({
      ids: [key],
      reason: rejectReason,
    });
  };

  const handleBulkReject = () => {
    const selectedIds = selectedRows
      .filter((row) => row.key !== "total")
      .map((row) => row.key);

    if (selectedIds.length > 0) {
      rejectLots({
        ids: selectedIds,
        reason: bulkRejectReason,
      });
    }
  };

  useEffect(() => {
    if (initalActionableInfluencers.length > 0) {
      setActionableInfluencers(initalActionableInfluencers);
      const newTableData = initalActionableInfluencers.map((item) =>
        generateTableData(item, lot?.brandBilling?.serviceChargeRate || 0)
      );
      setSelectedRowKeys(newTableData.map((item) => item.key));
      setSelectedRows(newTableData);
    } else {
      setActionableInfluencers([]);
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  }, [initalActionableInfluencers, lot?.brandBilling?.serviceChargeRate]);

  useEffect(() => {
    if (isApproved || isRejected) {
      setSelectedRowKeys([]);
      setSelectedRows([]);

      // if (isApproved && variables?.length === actionableInfluencers.length) {
      //   onClose();
      // }
    }
  }, [isApproved, isRejected, variables, rejectVariables]);

  if (isFetching || isFetchingBrand) {
    return (
      <Row justify={"center"}>
        <Spin />
      </Row>
    );
  }

  return (
    <div className={styles.purchaseConfirmation}>
      <Row gutter={8}>
        {/* Left Side - Table */}
        <Col span={16}>
          {isBillingDifferent && (
            <Alert
              message={`Lot Billing is different from the brand billing Service Charge Rate (${lot?.brandBilling?.serviceChargeRate}%) - (${brandBilling?.serviceChargeRate}%)`}
              type="warning"
              style={{ marginBottom: "20px" }}
            />
          )}
          {actionableInfluencers.length > 0 && (
            <Table
              size="small"
              columns={columns}
              dataSource={dataWithTotal}
              pagination={false}
              scroll={{ x: 800, y: "40vh" }}
              rowSelection={rowSelection}
              rowClassName={(record) =>
                record.key === "total" ? styles.totalRow : ""
              }
            />
          )}
          {restTableData.length > 0 && (
            <Table
              size="small"
              style={{ marginTop: "20px" }}
              columns={columns.slice(0, -1)}
              dataSource={restTableData}
              pagination={false}
              scroll={{ x: 800, y: "30vh" }}
              rowClassName={(record) =>
                record.isApproved ? styles.approvedRow : styles.disabledRow
              }
            />
          )}
        </Col>

        {/* Right Side - Panel */}
        <Col span={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {/* Selected Summary */}
            <Card
              title={`Purchase Summary (${selectedRows.length} items)`}
              size="small"
            >
              {isViewOnly ? (
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <div className={styles.summaryItem}>
                    <Text> Brand Price</Text>
                    <Text strong>
                      ₹{formatData(restApprovedAggregates.brandPrice, "0.00 ")}
                    </Text>
                  </div>
                  <div className={styles.summaryItem}>
                    <Text> Influencer Cost</Text>
                    <Text strong>
                      ₹
                      {formatData(
                        restApprovedAggregates.influencerCost,
                        "0.00 "
                      )}
                    </Text>
                  </div>
                  <div className={styles.summaryItem}>
                    <Text> Service Cost</Text>
                    <Text strong>
                      ₹{formatData(restApprovedAggregates.serviceCost, "0.00 ")}
                    </Text>
                  </div>
                  <div className={styles.summaryItem}>
                    <Text> Revenue</Text>
                    <Text strong>
                      ₹{formatData(restApprovedAggregates.revenue, "0.00 ")} (
                      {restApprovedRevenuePercentage.toFixed(1)}%)
                    </Text>
                  </div>
                  <Divider style={{ margin: "8px 0" }} />
                  <Flex vertical gap={8}>
                    <Popover
                      content={
                        <>
                          <Image
                            src={lot?.purchaseProof || ""}
                            alt="Purchase Proof"
                            width={100}
                          />
                        </>
                      }
                    >
                      <Button
                        type="link"
                        size="large"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          window.open(lot?.purchaseProof || "", "_blank");
                        }}
                      >
                        Purchase Proof
                      </Button>
                    </Popover>
                    {lot?.remarks && (
                      <div className={styles.remarks}>
                        <Space size={0}>
                          <Text strong>Remarks: </Text>
                          <Text type="secondary">
                            <TextWithTooltip text={lot.remarks} limit={50} />
                          </Text>
                        </Space>
                      </div>
                    )}
                    <Button
                      type="primary"
                      size="large"
                      block
                      disabled={selectedRows.length === 0 || isBillingDifferent}
                      loading={isApproving}
                      onClick={() => {
                        approveLots(
                          selectedRows
                            .filter((row) => row.key !== "total")
                            .map((row) => row.key)
                        );
                      }}
                    >
                      Approve ({selectedRows.length} items)
                    </Button>
                    <Popconfirm
                      title="Bulk Reject Creators"
                      description={
                        <div>
                          <p>
                            Are you sure you want to reject{" "}
                            {selectedRows.length} selected creators?
                          </p>
                          <Input.TextArea
                            placeholder="Enter rejection reason..."
                            value={bulkRejectReason}
                            onChange={(e) =>
                              setBulkRejectReason(e.target.value)
                            }
                            rows={3}
                            style={{ marginTop: 8 }}
                          />
                        </div>
                      }
                      onConfirm={handleBulkReject}
                      okText="Reject "
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                      disabled={selectedRows.length === 0}
                    >
                      <Button
                        type="primary"
                        size="large"
                        block
                        danger
                        disabled={selectedRows.length === 0}
                      >
                        Reject ({selectedRows.length} items)
                      </Button>
                    </Popconfirm>
                  </Flex>
                </Space>
              ) : (
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <div className={styles.summaryItem}>
                    <Text> Brand Price</Text>
                    <Text strong>
                      ₹{formatData(selectedAggregates.brandPrice, "0.00 ")}
                    </Text>
                  </div>
                  <div className={styles.summaryItem}>
                    <Text> Influencer Cost</Text>
                    <Text strong>
                      ₹{formatData(selectedAggregates.influencerCost, "0.00 ")}
                    </Text>
                  </div>
                  <div className={styles.summaryItem}>
                    <Text> Service Cost</Text>
                    <Text strong>
                      ₹{formatData(selectedAggregates.serviceCost, "0.00 ")}
                    </Text>
                  </div>
                  <div className={styles.summaryItem}>
                    <Text> Revenue</Text>
                    <Text strong>
                      ₹{formatData(selectedAggregates.revenue, "0.00 ")} (
                      {selectedRevenuePercentage.toFixed(1)}%)
                    </Text>
                  </div>
                  <Divider style={{ margin: "8px 0" }} />
                  <Flex vertical gap={8}>
                    <Popover
                      content={
                        <>
                          <Image
                            src={lot?.purchaseProof || ""}
                            alt="Purchase Proof"
                            width={100}
                          />
                        </>
                      }
                    >
                      <Button
                        type="link"
                        size="large"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          window.open(lot?.purchaseProof || "", "_blank");
                        }}
                      >
                        Purchase Proof
                      </Button>
                    </Popover>
                    {lot?.remarks && (
                      <div className={styles.remarks}>
                        <Space size={0}>
                          <Text strong>Remarks: </Text>
                          <Text type="secondary"> {lot.remarks}</Text>
                        </Space>
                      </div>
                    )}
                    <Button
                      type="primary"
                      size="large"
                      block
                      disabled={selectedRows.length === 0 || isBillingDifferent}
                      loading={isApproving}
                      onClick={() => {
                        approveLots(
                          selectedRows
                            .filter((row) => row.key !== "total")
                            .map((row) => row.key)
                        );
                      }}
                    >
                      Approve ({selectedRows.length} items)
                    </Button>
                    <Popconfirm
                      title="Bulk Reject Creators"
                      description={
                        <div>
                          <p>
                            Are you sure you want to reject{" "}
                            {selectedRows.length} selected creators?
                          </p>
                          <Input.TextArea
                            placeholder="Enter rejection reason..."
                            value={bulkRejectReason}
                            onChange={(e) =>
                              setBulkRejectReason(e.target.value)
                            }
                            rows={3}
                            style={{ marginTop: 8 }}
                          />
                        </div>
                      }
                      onConfirm={handleBulkReject}
                      okText="Reject "
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                      disabled={selectedRows.length === 0}
                    >
                      <Button
                        type="primary"
                        size="large"
                        block
                        danger
                        disabled={selectedRows.length === 0}
                      >
                        Reject ({selectedRows.length} items)
                      </Button>
                    </Popconfirm>
                  </Flex>
                </Space>
              )}
            </Card>

            {/* Total Summary */}
          </Space>
        </Col>
      </Row>
      {/* <Modal
        title={deliverableModalProps.type === "view" ? "View Deliverable" : ""}
        open={deliverableModalProps.visible}
        onCancel={() =>
          setDeliverableModalProps({
            visible: false,
            type: "create",
            deliverableId: "",
            briefInfluencerIds: [],
          })
        }
        footer={null}
        width={1000}
        destroyOnClose
      >
        {deliverableModalProps.visible && (
          <>
            <DeliverablePackageCard
              briefId={lot?.briefId || ""}
              defaultDeliverableId={lot?.brief?.defaultDeliverableV2Id}
              showMarkDefaultButton
              deliverableId={deliverableModalProps.deliverableId || ""}
              onClose={() =>
                setDeliverableModalProps({
                  visible: false,
                  type: "create",
                  deliverableId: "",
                  briefInfluencerIds: [],
                })
              }
            />
          </>
        )}
      </Modal> */}
      <style>{`.ant-table-row-selected >.ant-table-cell { background: var(--hover-bg) !important; }`}</style>

      <Modal
        open={showCannotPurchaseModal}
        title="Cannot purchase"
        onCancel={() => setShowCannotPurchaseModal(false)}
        footer={
          <Button onClick={() => setShowCannotPurchaseModal(false)}>
            Okay
          </Button>
        }
      >
        {purchaseResult?.result.map(({ status, reason, briefInfluencer }) => (
          <div key={briefInfluencer._id} style={{ marginBottom: 16 }}>
            <b>{briefInfluencer.influencerName} cannot be purchased</b>
            <div>{reason}</div>
          </div>
        ))}
      </Modal>
      {/* NON_PROFITABLE Modal */}
      <Modal
        open={showNonProfitableModal}
        title="Non Profitable Deal"
        onCancel={() => setShowNonProfitableModal(false)}
        okButtonProps={{
          type: "primary",
          loading: isPurchasingNonProfitable,
        }}
        onOk={async () => {
          if (purchaseResult?.result?.length) {
            for (const {
              purchaseLotId,
              purchaseLotItemId,
            } of purchaseResult.result) {
              await purchaseNonProfitable({
                purchaseLotId: purchaseLotId,
                purchaseLotItemsId: purchaseLotItemId,
              });
            }
          }
        }}
      >
        {purchaseResult?.result.map(({ status, reason, briefInfluencer }) => (
          <div key={briefInfluencer._id} style={{ marginBottom: 16 }}>
            <div>Brand Price is {briefInfluencer.brandCost?.cost}</div>
            <div>
              Influencer Commercials is{" "}
              {briefInfluencer.brandCost?.influencerCost?.cost}
            </div>
            <div>Revenue is {briefInfluencer?.brandCost?.revenue}</div>
            <b>Are you sure you want to make this purchase?</b>
          </div>
        ))}
      </Modal>
    </div>
  );
}
